import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Html5Qrcode } from 'html5-qrcode';

interface QrCameraProps {
  /** Appelé une seule fois, avec le texte brut du premier QR lu. */
  onDecode: (text: string) => void;
  /** Le flux n'a pas pu s'ouvrir : refusé par le téléphone, ou pas de caméra
   *  (page servie en http : `getUserMedia` n'existe pas). */
  onFailure: (reason: 'denied' | 'unavailable') => void;
}

const READER_ID = 'waiter-loyalty-qr-reader';

/**
 * La caméra du « Scanner » fidélité — même bibliothèque que le scanner
 * owner/admin (`html5-qrcode`), chargée à la demande : le carnet ne la paie
 * qu'au premier scan, le site client jamais.
 *
 * La caméra vit exactement aussi longtemps que ce composant : la feuille le
 * monte pour scanner et le démonte dès qu'un code est lu ou que le serveur
 * ferme, si bien que le voyant d'enregistrement ne peut pas rester allumé.
 */
export default function QrCamera({ onDecode, onFailure }: QrCameraProps) {
  const { t } = useTranslation();
  const [starting, setStarting] = useState(true);
  // Les rappels du parent changent à chaque rendu ; la caméra, elle, ne doit
  // démarrer qu'une fois.
  const onDecodeRef = useRef(onDecode);
  const onFailureRef = useRef(onFailure);
  onDecodeRef.current = onDecode;
  onFailureRef.current = onFailure;

  useEffect(() => {
    let disposed = false;
    let handled = false;
    let scanner: Html5Qrcode | null = null;

    const teardown = async () => {
      const current = scanner;
      scanner = null;
      if (!current) return;
      try {
        if (current.isScanning) await current.stop();
      } catch (error) {
        console.warn('QrCamera: stop failed', error);
      }
      try {
        current.clear();
      } catch (error) {
        console.warn('QrCamera: clear failed', error);
      }
    };

    void (async () => {
      try {
        const lib = await import('html5-qrcode');
        if (disposed) return;
        scanner = new lib.Html5Qrcode(READER_ID, {
          formatsToSupport: [lib.Html5QrcodeSupportedFormats.QR_CODE],
          verbose: false,
        });
        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: (width: number, height: number) => {
              const side = Math.floor(Math.min(width, height) * 0.7);
              return { width: side, height: side };
            },
          },
          (text: string) => {
            if (handled) return;
            handled = true;
            navigator.vibrate?.(60);
            onDecodeRef.current(text);
          },
          () => {},
        );
        if (disposed) {
          await teardown();
          return;
        }
        setStarting(false);
      } catch (error) {
        if (disposed) return;
        console.warn('QrCamera: camera could not start', error);
        onFailureRef.current(/NotAllowed|Permission/i.test(String(error)) ? 'denied' : 'unavailable');
      }
    })();

    return () => {
      disposed = true;
      void teardown();
    };
  }, []);

  return (
    // Carré, plafonné à la moitié de l'écran : le champ de saisie du code
    // doit rester visible dessous sur un téléphone de 568px de haut.
    <div
      data-testid="loyalty-scan-camera"
      className="relative mx-auto w-full max-w-[50dvh] aspect-square overflow-hidden rounded-btn bg-ink"
    >
      <div id={READER_ID} className="w-full h-full [&_video]:!w-full [&_video]:!h-full [&_video]:object-cover" />
      {starting && (
        <p
          role="status"
          className="absolute inset-0 flex items-center justify-center font-sans text-base font-bold text-paper"
        >
          {t('waiter.loyaltyScan.camera.starting')}
        </p>
      )}
    </div>
  );
}
